import { IconArrowLeft, IconArrowRight } from '@tock/icons';
import {
  IconArrowLeft as IconArrowLeftLegacy,
  IconArrowRight as IconArrowRightLegacy,
} from 'ui/icons';
import { IconButton } from 'omakase';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { ResponsiveSizeMap } from 'omakase/utils/responsiveSizeMap';
import { Theme, css } from '@emotion/react';
import { headerOrFooter, mainContainer, scrollContainer } from './horizontalScrollContainerCssDefs';
import { useCurrentBreakpoint } from 'hooks/useCurrentBreakpoint';
import { useInView } from 'react-intersection-observer';
import React, { useEffect, useRef, useState } from 'react';

type ArrowsPosition = 'top' | 'bottom';
export interface HorizontalScrollContainerProps {
  heading?: React.ReactNode;
  /**
   * Whether navigation arrows are above or below scroll container. Defaults to 'top'
   */
  arrowsPosition?: ArrowsPosition;
  buttonVariant?: 'ghost' | 'basic' | 'ghostInverted';
  pageContainer?: boolean;
  responsiveCardCount?: ResponsiveCardSizeMap;
  carouselPadding?: Pick<ResponsiveCardSizeMap, 'xs' | 'sm'>;
  /**
   * Specified in theme spacing units
   */
  verticalMargin?: number;
  onScrollItemIntoView?(index: number): void;
  className?: string;
}

export type CarouselPaddingSizeMap = Pick<ResponsiveCardSizeMap, 'xs' | 'sm'>;

export type ResponsiveCardSizeMap = ResponsiveSizeMap<number>;

const DEFAULT_CARDS_AT_BREAKPOINTS: Required<ResponsiveCardSizeMap> = {
  xs: 2,
  sm: 2,
  md: 3,
  lg: 4,
  lgx: 4,
  xl: 5,
};
const DEFAULT_CAROUSEL_PADDING_AT_BREAKPOINTS = { xs: 20, sm: 60 };

const ScrollItem: React.FC<{
  children?: React.ReactNode;
  index: number;
  length: number;
  setLeftButtonDisabled: (leftInView: boolean) => void;
  setRightButtonDisabled: (rightInView: boolean) => void;
  arrowsPosition: ArrowsPosition;
  onScrollIntoView?(index: number): void;
  clicksAllowed?: boolean;
}> = ({
  children,
  index,
  length,
  setLeftButtonDisabled,
  setRightButtonDisabled,
  arrowsPosition,
  onScrollIntoView,
  clicksAllowed = true,
}) => {
  // for some unknown reason, a threshold of 1 breaks in safari and mobile safari
  const { ref, inView } = useInView({ threshold: 0.99 });
  const [hasBeenInView, setHasBeenInView] = React.useState(false);

  const leftInView = inView && index === 0;
  const rightInView = inView && index === length - 1;

  useEffect(() => {
    setLeftButtonDisabled(leftInView);
  }, [leftInView, setLeftButtonDisabled]);

  useEffect(() => {
    setRightButtonDisabled(rightInView);
  }, [rightInView, setRightButtonDisabled]);

  useEffect(() => {
    if (inView && !hasBeenInView) {
      onScrollIntoView?.(index);
      setHasBeenInView(true);
    }
  }, [inView, index, hasBeenInView, onScrollIntoView]);

  return (
    <li
      className={inView ? 'in-view' : ''}
      css={
        !clicksAllowed &&
        css`
          pointer-events: none;
        `
      }
    >
      {/* dummy element for attaching IntersectionObserver so that threshold is met
      when 1px of card is visible vertically */}
      <div
        ref={ref}
        css={[
          css`
            position: absolute;
            left: 0;
            height: 1px;
            width: 100%;
            background-color: transparent;
            visibility: hidden;
          `,
          arrowsPosition === 'top' &&
            css`
              top: 0;
            `,
          arrowsPosition === 'bottom' &&
            css`
              bottom: 0;
            `,
        ]}
      ></div>
      {children}
    </li>
  );
};

function getEffectiveResponsiveCardCount({
  xs,
  sm,
  md,
  lg,
  lgx,
  xl,
}: ResponsiveCardSizeMap): Required<ResponsiveCardSizeMap> {
  const defaultsForFallbacks: Required<ResponsiveCardSizeMap> = {
    xs: xs ?? DEFAULT_CARDS_AT_BREAKPOINTS.xs,
    sm: sm ?? DEFAULT_CARDS_AT_BREAKPOINTS.sm,
    md: md ?? DEFAULT_CARDS_AT_BREAKPOINTS.md,
    lg: lg ?? DEFAULT_CARDS_AT_BREAKPOINTS.lg,
    lgx: lgx ?? DEFAULT_CARDS_AT_BREAKPOINTS.lgx,
    xl: xl ?? DEFAULT_CARDS_AT_BREAKPOINTS.xl,
  };

  return {
    xs: xs ?? defaultsForFallbacks.xs,
    sm: sm ?? defaultsForFallbacks.xs,
    md: md ?? defaultsForFallbacks.sm,
    lg: lg ?? defaultsForFallbacks.md,
    lgx: lgx ?? defaultsForFallbacks.lg,
    xl: xl ?? defaultsForFallbacks.lgx,
  };
}

const HorizontalScrollContainer: React.FC<HorizontalScrollContainerProps> = ({
  children,
  heading,
  arrowsPosition = 'top',
  buttonVariant = 'ghost',
  pageContainer = true,
  responsiveCardCount = DEFAULT_CARDS_AT_BREAKPOINTS,
  carouselPadding = DEFAULT_CAROUSEL_PADDING_AT_BREAKPOINTS,
  verticalMargin = 5,
  onScrollItemIntoView,
  ...rest
}) => {
  const [leftButtonDisabled, setLeftButtonDisabled] = useState(true);
  const [rightButtonDisabled, setRightButtonDisabled] = useState(true);
  const containerRef = useRef<HTMLUListElement>(null);
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const momentumID = useRef<number | undefined>();
  const velX = useRef(0);
  const [clicksAllowed, setClicksAllowed] = useState(true);
  const [xClickPos, setXClickPos] = useState(0);

  const effectiveResponsiveCardCount = getEffectiveResponsiveCardCount(responsiveCardCount);

  const currentBreakpoint = useCurrentBreakpoint();

  const childCount = React.Children.count(children);
  const doChildrenOverflowContainer = childCount > effectiveResponsiveCardCount[currentBreakpoint];

  const scrollItemChildren = React.Children.map(children, (child, index) => (
    <ScrollItem
      index={index}
      length={React.Children.count(children)}
      setLeftButtonDisabled={setLeftButtonDisabled}
      setRightButtonDisabled={setRightButtonDisabled}
      arrowsPosition={arrowsPosition}
      onScrollIntoView={onScrollItemIntoView}
      clicksAllowed={clicksAllowed}
    >
      {child}
    </ScrollItem>
  ));

  const onMouseDown = (e: React.MouseEvent<HTMLUListElement>) => {
    e.preventDefault();
    setXClickPos(e.pageX);
    const { current: container } = containerRef;
    if (container) {
      container.style.scrollSnapType = 'none';
      container.style.cursor = 'grabbing';
      setIsDown(true);
      setStartX(e.pageX - container.offsetLeft);
      setScrollLeft(container.scrollLeft);
      endMomentum();
    }
  };

  // momentum scrolling approach inspired by https://codepen.io/loxks/pen/KKpVvVW
  const startMomentum = () => {
    endMomentum();
    momentumID.current = window.requestAnimationFrame(momentumLoop);
  };

  const endMomentum = () => {
    if (momentumID.current) {
      window.cancelAnimationFrame(momentumID.current);
    }
  };

  const momentumLoop = () => {
    const { current: container } = containerRef;
    if (container) {
      container.scrollLeft += velX.current;
      velX.current = velX.current * 0.95;
      if (Math.abs(velX.current) > 0.5) {
        momentumID.current = window.requestAnimationFrame(momentumLoop);
      }
    }
  };

  const onMouseUp = (e: React.MouseEvent<HTMLUListElement>) => {
    e.preventDefault();
    setClicksAllowed(true);
    const { current: container } = containerRef;
    if (isDown && container) {
      container.style.scrollSnapType = 'none';
      container.style.cursor = 'auto';
      startMomentum();
    }
    setIsDown(false);
  };

  const onMouseMove = (e: React.MouseEvent<HTMLUListElement>) => {
    e.preventDefault();
    if (!isDown) {
      return;
    }
    const xPosDelta = Math.abs(xClickPos - e.pageX);
    if (xPosDelta > 2) {
      setClicksAllowed(false);
    }
    const { current: container } = containerRef;
    if (isDown && container) {
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 1.1;
      const prevScrollLeft = container.scrollLeft;

      container.scrollLeft = scrollLeft - walk;
      velX.current = container.scrollLeft - prevScrollLeft;
    }
  };

  const onMouseLeave = (e: React.MouseEvent<HTMLUListElement>) => {
    if (isDown) {
      e.preventDefault();
      setIsDown(false);
    }
  };

  function outerWidth(el: HTMLElement) {
    let width = el.offsetWidth;
    const style = getComputedStyle(el);

    width += parseInt(style.marginLeft) + parseInt(style.marginRight);
    return width;
  }

  function roundToNearest(value: number, interval: number) {
    return Math.round(Math.round(value / interval) * interval);
  }

  const handleNextClick = async () => {
    if (rightButtonDisabled) {
      return;
    }
    if (momentumID.current) {
      endMomentum();
    }
    const { current: container } = containerRef;
    if (container) {
      const scrollLeft = container.scrollLeft;

      const target = container.querySelector(`li.in-view`)
        ?.nextElementSibling as HTMLElement | null;
      if (target) {
        const targetWidth = outerWidth(target);
        const modulo = scrollLeft % targetWidth;
        // allow a little rounding to account for partial pixels in the DOM
        const effectiveModulo = modulo > targetWidth - 10 ? 0 : modulo;
        const newScrollLeft = scrollLeft + targetWidth - effectiveModulo;

        container.scrollTo({
          left: roundToNearest(newScrollLeft, targetWidth),
          behavior: 'smooth',
        });
      }
    }
  };

  const handlePrevClick = () => {
    if (leftButtonDisabled) {
      return;
    }
    if (momentumID.current) {
      endMomentum();
    }
    const { current: container } = containerRef;
    if (container) {
      const scrollLeft = container.scrollLeft;
      const target = container.querySelector(`li.in-view`)
        ?.previousElementSibling as HTMLElement | null;
      if (target) {
        const isFirstChild = target.previousElementSibling === null;
        // account for first child not having right margin
        const targetWidth = isFirstChild ? outerWidth(target) + 16 : outerWidth(target);
        const modulo = scrollLeft % targetWidth;

        container.scrollTo({
          left: modulo
            ? roundToNearest(scrollLeft - modulo, targetWidth)
            : roundToNearest(scrollLeft - targetWidth, targetWidth),
          behavior: 'smooth',
        });
      }
    }
  };

  const arrowsNav = (
    <div
      css={({ breakpoints }: Theme) => css`
        margin-left: auto;
        display: none;
        ${breakpoints.up('sm')} {
          display: block;
        }
      `}
    >
      <IconButton
        variant={buttonVariant}
        size="large"
        aria-label="previous"
        onClick={handlePrevClick}
        css={css`
          margin-right: 1rem;
          // TODO: sync with DJ on updating disbaled styles and syncing [aria-disabled] styles
          &[aria-disabled='true'] {
            opacity: 0.5;
          }
        `}
        aria-disabled={leftButtonDisabled}
      >
        <IconMigrationUtil legacyIcon={<IconArrowLeftLegacy />} newIcon={<IconArrowLeft />} />
      </IconButton>
      <IconButton
        variant={buttonVariant}
        size="large"
        aria-label="next"
        onClick={handleNextClick}
        aria-disabled={rightButtonDisabled}
        css={css`
          &[aria-disabled='true'] {
            opacity: 0.5;
          }
        `}
      >
        <IconMigrationUtil legacyIcon={<IconArrowRightLegacy />} newIcon={<IconArrowRight />} />
      </IconButton>
    </div>
  );

  return (
    <div css={mainContainer({ pageContainer, carouselPadding, verticalMargin })} {...rest}>
      <div css={headerOrFooter({ isHeader: true })}>
        {heading}
        {doChildrenOverflowContainer && arrowsPosition === 'top' && arrowsNav}
      </div>
      <ul
        ref={containerRef}
        className="horizontalScrollContainer"
        css={scrollContainer(effectiveResponsiveCardCount)}
        onMouseDownCapture={onMouseDown}
        onMouseUpCapture={onMouseUp}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
      >
        {scrollItemChildren}
        <li className="buffer"></li>
      </ul>
      {doChildrenOverflowContainer && arrowsPosition === 'bottom' && (
        <div css={headerOrFooter({ isHeader: false })}>{arrowsNav}</div>
      )}
    </div>
  );
};

export default HorizontalScrollContainer;
